/**
 * Bringing Project Hub's project nesting in line with Todoist's.
 *
 * Todoist owns the structure of the projects it mirrors. Each mirrored project
 * keeps `todoistParentId`, rewritten on every sync it appears in, so after the
 * sync's first pass it says where Todoist has the project now. This works out
 * which records disagree:
 *
 *   - Todoist nests it under a mirrored project → put it there (and drop any
 *     folder: a sub-project sits wherever its parent does).
 *   - Todoist has it at the top level, but here it sits under another mirrored
 *     project → take it out. This is the case the sync used to miss, so a
 *     project moved out of being a sub in Todoist stayed a sub here.
 *   - It sits under a project of Project Hub's own (no Todoist id) → leave it:
 *     that nesting was made here, deliberately, and Todoist knows nothing of it.
 *
 * Every mirrored project is checked, not only the ones a sync touched, so a
 * record that drifted for any reason is repaired on the next run. A fix is
 * produced only where something is actually wrong.
 *
 * Pure: imports types only, so tests/project-nesting.test.mjs loads it directly.
 */

import type { Project } from "./types";

export interface NestingFix {
  id: string;
  name: string;
  /** The parent it should have; null for the top level. */
  parent: string | null;
  /** Clear its folder too (it is going under a parent). */
  clearFolder: boolean;
}

type Mirrored = Pick<Project, "id" | "name" | "parent" | "folderId" | "todoistId" | "todoistParentId">;

export function nestingFixes(mirrored: Mirrored[]): NestingFix[] {
  const byTodoistId = new Map(mirrored.filter((p) => p.todoistId).map((p) => [p.todoistId!, p]));
  const mirroredIds = new Set(mirrored.filter((p) => p.todoistId).map((p) => p.id));
  const fixes: NestingFix[] = [];

  for (const p of mirrored) {
    if (!p.todoistId) continue;

    if (p.todoistParentId) {
      const target = byTodoistId.get(p.todoistParentId);
      // A parent Todoist names but we don't have yet: leave it for a later run
      // rather than guess.
      if (!target || target.id === p.id) continue;
      if (p.parent !== target.id || p.folderId) {
        fixes.push({ id: p.id, name: p.name, parent: target.id, clearFolder: true });
      }
      continue;
    }

    // Top level in Todoist. Only un-nest from another mirrored project; a
    // parent created here is Project Hub's own arrangement.
    if (p.parent && mirroredIds.has(p.parent)) {
      fixes.push({ id: p.id, name: p.name, parent: null, clearFolder: false });
    }
  }
  return fixes;
}
